import { CompanyContextType } from '../contexts/auth';
import { type ItemListType } from './common';

/**
 * アップロードタスクのステータス
 */
export const uploadTaskStatus = {
  uploading: 'uploading', // Storageアップロード中
  creating: 'creating', // resultレコード作成中
  calling: 'calling', // API呼び出し中
  processing: 'processing', // OCR読取中
  completed: 'completed', // 完了
  error: 'error', // エラー
} as const;

export type UploadTaskStatus = (typeof uploadTaskStatus)[keyof typeof uploadTaskStatus];

/**
 * アップロードタスクの種別
 */
export const uploadTaskType = {
  classification: 'classification', // ホーム画面（自動仕分け）
  pattern: 'pattern', // パターンOCR画面
} as const;

export type UploadTaskType = (typeof uploadTaskType)[keyof typeof uploadTaskType];

/**
 * Storage アップロード結果（1ファイル分）
 */
export interface StorageUploadResult {
  fileName: string;
  filePath: string;
  downloadUrl: string;
  fileSize: number;
  fileType: string;
  pageCount: number;
}

/**
 * アップロードタスク（1回のアップロード操作 = 1タスク）
 */
export interface UploadTask {
  id: string;
  type: UploadTaskType;
  status: UploadTaskStatus;
  label: string; // 表示名（例: "自動仕分 3件"）
  fileNames: string[]; // アップロードファイル名一覧
  fileCount: number; // アップロードファイル数
  totalPageCount: number; // 作成されるレコード数（ページ数）
  uploadedCount: number; // Storageアップロード済みファイル数
  resultIds?: string[]; // 作成したresultのID一覧（OCR完了監視用）
  processedCount?: number; // OCR処理完了したresult数
  errorResultCount?: number; // OCRエラーになったresult数
  totalResultCount?: number; // 全result数
  errorMessage?: string;
  patternId?: string; // パターンOCR画面のパターンID
  hidePanel?: boolean; // trueのときUploadProgressPanelに表示しない
  createdAt: number; // Date.now()
}

/**
 * ホーム画面（仕分け）用アップロードパラメータ
 */
export interface ClassificationUploadParams {
  type: 'classification';
  files: File[];
  originalFiles: File[];
  totalPageCount: number;
  pageFileNames: string[];
  userId: string;
  userName: string;
  userIconUrl: string | null | undefined;
  /** 担当者UID（#3061）: 通常はアップロード本人、代理時は企業担当者。取得不可時は null */
  assigneeId: string | null;
  /** 担当者表示名（非正規化保存用スナップショット）。未設定時は null */
  assigneeName: string | null;
  /** 担当者アイコンURL（非正規化保存用スナップショット）。未設定時は null */
  assigneeIconUrl: string | null;
  processingType: number;
  geminiModel: number;
  companyId: string;
  allowAiLearning: boolean;
  isProxyMode: boolean;
  operatorId?: string;
  company: CompanyContextType | null;
  baseRemainingCount: number | null;
  /** 月間枠の残り円（floorしない / #2948）。端数も枠で使い切るためのクレジット計算に使う。 */
  baseRemainingJpy?: number | null;
}

/**
 * パターンOCR画面用アップロードパラメータ
 */
export interface PatternUploadParams {
  type: 'pattern';
  files: File[];
  originalFiles: File[];
  totalPageCount: number;
  pageFileNames: string[];
  userId: string;
  userName: string;
  userIconUrl: string | null | undefined;
  /** 担当者UID（#2642）: アップロード画面で選択。未選択時は null（#2642 レビューFB） */
  assigneeId: string | null;
  /** 担当者表示名（非正規化保存用スナップショット）。未選択時は null */
  assigneeName: string | null;
  /** 担当者アイコンURL（非正規化保存用スナップショット）。未選択時は null */
  assigneeIconUrl: string | null;
  processingType: number;
  geminiModel: number;
  companyId: string | undefined;
  allowAiLearning: boolean;
  isProxyMode: boolean;
  operatorId?: string;
  operatorRole?: number;
  company: CompanyContextType | null;
  baseRemainingCount: number | null;
  /** 月間枠の残り円（floorしない / #2948）。端数も枠で使い切るためのクレジット計算に使う。 */
  baseRemainingJpy?: number | null;
  patternId: string;
  patternName: string;
  itemList: ItemListType[];
  thinkingBudget?: number | null;
  thinkingLevel?: string | null;
  /** OCRエンジン切替（#2960 運営スタッフ限定・ocrEngineType の数値。未指定は Vision） */
  ocrEngine?: number;
  /** Geminiサンプリングパラメータ（#2960 運営スタッフ限定・1回目OCR抽出用。未指定はモデルデフォルト） */
  geminiTemperature?: number | null;
  geminiTopP?: number | null;
  /** 行の矩形座標を Gemini に渡すか（#2960 方式B・運営スタッフ限定） */
  includeCoordinates?: boolean;
  /** 2回目構造化エンジン（#2960 structuredEngineType・運営スタッフ限定。未指定は gemini） */
  structuredEngine?: number;
  /** マスタ照合が有効なパターン (pattern.has_master_mapping === true) なら true。仮加算 +15円 用 */
  hasMasterMapping?: boolean;
  /** データリフレッシュ用コールバック */
  onCompleted?: () => Promise<void>;
  /** trueのときUploadProgressPanelに表示しない */
  hidePanel?: boolean;
}

export type UploadParams = ClassificationUploadParams | PatternUploadParams;

/**
 * アップロード進捗バーの各ステージにおけるプログレス値（%）
 */
export const uploadProgressValue = {
  uploadingMax: 60, // アップロード完了時の上限
  creating: 70, // レコード作成中
  calling: 90, // API呼び出し中
  completed: 100, // 完了
} as const;
